import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotify } from 'react-admin';
import { Box, Paper, TextField, Button, Typography, Stack } from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import authProvider from '../../authProvider';
import { changePassword, checkPasswordAgainstPolicy, getPasswordPolicyConfig, PasswordPolicyConfig } from '../../services/userService.ts';

const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const notify = useNotify();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicyConfig | null>(null);

  useEffect(() => {
    getPasswordPolicyConfig().then(setPasswordPolicy).catch(() => {});
  }, []);

  const newPasswordError = newPassword && passwordPolicy ? checkPasswordAgainstPolicy(newPassword, passwordPolicy) : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const policy = passwordPolicy ?? await getPasswordPolicyConfig();
      if (checkPasswordAgainstPolicy(newPassword, policy)) {
        // Violation is already shown live under the New Password field — no banner needed.
        return;
      }

      await changePassword(currentPassword, newPassword);
      localStorage.setItem('mustChangePassword', 'false');
      notify('Password changed successfully', { type: 'success' });
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Failed to change password.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await authProvider.logout();
    navigate('/login', { replace: true });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f8fafc',
        p: 2,
      }}
    >
      <Paper elevation={0} sx={{ p: 4, maxWidth: 420, width: '100%', borderRadius: '14px', border: '1px solid #e5e7eb' }}>
        <Stack alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <LockResetIcon sx={{ fontSize: 36, color: '#0f172a' }} />
          <Typography variant="h6" fontWeight={800} sx={{ color: '#0f172a' }}>Change Your Password</Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            For account security, you must set a new password before continuing.
          </Typography>
        </Stack>

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              label="Current Password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              fullWidth
              disabled={submitting}
              autoFocus
            />
            <TextField
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              disabled={submitting}
              error={!!newPasswordError}
              helperText={newPasswordError || ' '}
            />
            <TextField
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              disabled={submitting}
            />

            {error && (
              <Typography color="error" variant="body2">{error}</Typography>
            )}

            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              sx={{ borderRadius: '8px', textTransform: 'none', bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' }, py: 1.1 }}
            >
              {submitting ? 'Updating...' : 'Update Password'}
            </Button>

            <Button
              onClick={handleLogout}
              disabled={submitting}
              sx={{ textTransform: 'none', color: '#64748b' }}
            >
              Log out instead
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default ChangePasswordPage;
